import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { InfoWrapper } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Styled";
import { RESULT_DROPDOWN_ITEM_DATA_TEST_ID } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/consts";
import { ResultDropdownDeleteButton } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownDeleteButton";
import StringHighlight from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/StringHighlight/index";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Opacity } from "@faire/web--source/slate/Color";
import { Flex } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackSearchNarrowAutocompleteDropdownClick } from "@faire/web-api--source/events/search/click/narrowAutocompleteDropdown";
import { trackSearchNarrowAutocompleteDropdownView } from "@faire/web-api--source/events/search/view/narrowAutocompleteDropdown";
import * as React from "react";
import styled from "styled-components";
var SEARCH_ITEM_LINK_HEIGHT = getSpacing("7x");
var SEARCH_ITEM_LINK_HEIGHT_SMALL = getSpacing("6x");
var SEARCH_ITEM_LINK_PADDING_LEFT = getSpacing("2x");
var IMAGE_SEARCH_TERM_WRAPPER_PADDING_RIGHT = getSpacing("2x");
var STYLED_DELETE_BUTTON_PADDING_RIGHT = getSpacing("3x");
var CATEGORY_FILTER_PREFIX = "category:";
var TRACKER_DELIMITER = "|";
export var ResultDropdownImageSearchItem = function(param) {
    var suggestion = param.suggestion, onDelete = param.onDelete, _param_isDeleting = param.isDeleting, isDeleting = _param_isDeleting === void 0 ? false : _param_isDeleting, onHover = param.onHover, onMouseLeave = param.onMouseLeave, onClick = param.onClick, _param_image = param.image, image = _param_image === void 0 ? EAEAEA_PIXEL : _param_image, reverseHighlightText = param.reverseHighlightText, selected = param.selected, shouldEnableSuggestionsImpressionAllocationV2 = param.shouldEnableSuggestionsImpressionAllocationV2;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var searchData = suggestion.searchData;
    var hasValidSuggestionSubtitle = searchData.category_display_name !== undefined && searchData.category_key !== undefined;
    var shouldTrackAutocompleteSuggestion = hasValidSuggestionSubtitle && suggestion.requestID !== undefined && reverseHighlightText !== undefined;
    React.useEffect(function() {
        if (shouldTrackAutocompleteSuggestion) {
            trackSearchNarrowAutocompleteDropdownView(reverseHighlightText + TRACKER_DELIMITER + suggestion.label, searchData.category_key.replace(CATEGORY_FILTER_PREFIX, "").trim(), suggestion.requestID);
        }
    }, [
        reverseHighlightText,
        searchData.category_key,
        shouldTrackAutocompleteSuggestion,
        suggestion.label,
        suggestion.requestID
    ]);
    /**
     * Callback function that performs click-tracking for autcomplete
     * as well as the provided onClick() prop
     */ var onClickCallback = function() {
        if (shouldTrackAutocompleteSuggestion) {
            trackSearchNarrowAutocompleteDropdownClick(reverseHighlightText + TRACKER_DELIMITER + suggestion.label, searchData.category_key.replace(CATEGORY_FILTER_PREFIX, "").trim(), suggestion.requestID);
        }
        onClick();
    };
    return /*#__PURE__*/ _jsx(Wrapper, {
        "data-test-id": "search-recommendation-full-width-item",
        children: /*#__PURE__*/ _jsxs(SearchItemLink, {
            onClick: isDeleting ? undefined : onClickCallback,
            onMouseEnter: onHover,
            onMouseLeave: onMouseLeave,
            to: suggestion.url,
            tabIndex: -1,
            $selected: selected,
            $height: shouldEnableSuggestionsImpressionAllocationV2 ? SEARCH_ITEM_LINK_HEIGHT_SMALL : SEARCH_ITEM_LINK_HEIGHT,
            children: [
                /*#__PURE__*/ _jsxs(ImageSearchTermWrapper, {
                    children: [
                        typeof image === "string" ? /*#__PURE__*/ _jsx(StyledImage, {
                            src: image,
                            "data-test-id": "result-dropdown-image-search-item-image-".concat(suggestion.label)
                        }) : /*#__PURE__*/ _jsx(SVGIconWrapper, {
                            children: image
                        }),
                        /*#__PURE__*/ _jsxs(InfoWrapper, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    "data-test-id": RESULT_DROPDOWN_ITEM_DATA_TEST_ID,
                                    children: reverseHighlightText !== undefined && reverseHighlightText !== "" ? /*#__PURE__*/ _jsx(StringHighlight, {
                                        message: suggestion.label,
                                        highlight: reverseHighlightText,
                                        reverseHighlight: true
                                    }) : suggestion.label
                                }),
                                /*#__PURE__*/ _jsx(MaybeSuggestionSubtitle, {
                                    hasValidSuggestionSubtitle: hasValidSuggestionSubtitle,
                                    searchData: searchData
                                })
                            ]
                        })
                    ]
                }),
                onDelete ? /*#__PURE__*/ _jsx(StyledDeleteButton, {
                    onDelete: onDelete,
                    isDeleting: isDeleting,
                    ariaLabel: strictLocalize({
                        id: "wlZjqN",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Delete \""
                            },
                            {
                                type: 1,
                                value: "labelFullyTranslatedSentence"
                            },
                            {
                                type: 0,
                                value: "\" search query"
                            }
                        ]
                    }, {
                        labelFullyTranslatedSentence: suggestion.label
                    })
                }) : null
            ]
        })
    });
};
var MaybeSuggestionSubtitle = function(param) {
    var hasValidSuggestionSubtitle = param.hasValidSuggestionSubtitle, searchData = param.searchData;
    if (hasValidSuggestionSubtitle) {
        // Translations of category_display_name are handled in the BE
        return /*#__PURE__*/ _jsx(SubtitleWrapper, {
            children: searchData.category_display_name
        });
    }
    return null;
};
var StyledDeleteButton = styled(ResultDropdownDeleteButton).withConfig({
    componentId: "sc-3b3d3b10-0"
})([
    "display:flex;align-items:center;justify-content:center;width:40px;height:40px;padding-right:",
    ";"
], STYLED_DELETE_BUTTON_PADDING_RIGHT);
var SearchItemLink = styled(Link).withConfig({
    componentId: "sc-3b3d3b10-1"
})([
    "display:flex;align-items:center;justify-content:space-between;width:100%;height:",
    ";padding-left:",
    ";background-color:",
    ";"
], function(param) {
    var $height = param.$height;
    return $height;
}, SEARCH_ITEM_LINK_PADDING_LEFT, function(param) {
    var $selected = param.$selected;
    return $selected ? Core.surface.subdued + Opacity.O_50 : undefined;
});
var Wrapper = styled.li.withConfig({
    componentId: "sc-3b3d3b10-2"
})([
    "display:flex;align-items:center;position:relative;"
]);
var StyledImage = styled.img.withConfig({
    componentId: "sc-3b3d3b10-3"
})([
    "height:40px;width:40px;position:relative;border-radius:50%;"
]);
var ImageSearchTermWrapper = styled(Flex).attrs({
    justify: "flex-start",
    align: "center"
}).withConfig({
    componentId: "sc-3b3d3b10-4"
})([
    "overflow:hidden;padding-right:",
    ";"
], IMAGE_SEARCH_TERM_WRAPPER_PADDING_RIGHT);
var SVGIconWrapper = styled(Flex).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-3b3d3b10-5"
})([
    "height:40px;width:40px;border-radius:50%;background-color:",
    ";"
], Core.surface.subdued);
var SubtitleWrapper = styled(Typography).attrs({
    truncate: true,
    color: Core.text.subdued
}).withConfig({
    componentId: "sc-3b3d3b10-6"
})([
    "width:100%;"
]);
